/**
 * 请求队列管理器
 * 
 * 功能：
 * 1. 请求串行化（避免重复 Token 刷新）
 * 2. 请求去重（相同请求只发一次）
 * 3. 请求阻塞和恢复
 * 
 * 使用场景：
 * - Token 刷新时阻塞其他请求
 * - 避免用户快速点击导致重复请求
 */

/**
 * 请求唯一标识
 */
type RequestKey = string

/**
 * 队列中的请求
 */
interface QueuedRequest<T> {
  key: RequestKey
  executor: () => Promise<T>
  resolve: (value: T) => void
  reject: (error: any) => void
  timestamp: number
}

/**
 * 请求队列配置
 */
export interface RequestQueueConfig {
  /**
   * 是否启用请求去重（默认：true）
   */
  enableDeduplication?: boolean

  /**
   * 去重时间窗口（毫秒，默认：1000）
   * 在此时间内的相同请求会被合并
   */
  deduplicationWindow?: number

  /**
   * 是否启用调试日志（默认：false）
   */
  debug?: boolean
}

/**
 * 默认配置
 */
const DEFAULT_CONFIG: Required<RequestQueueConfig> = {
  enableDeduplication: true,
  deduplicationWindow: 1000,
  debug: false,
}

/**
 * 请求队列管理器
 */
export class RequestQueue {
  private config: Required<RequestQueueConfig>
  private queue: QueuedRequest<any>[] = []
  private isProcessing = false
  private isBlocked = false
  private pendingRequests = new Map<RequestKey, Promise<any>>()

  constructor(config: RequestQueueConfig = {}) {
    this.config = {
      ...DEFAULT_CONFIG,
      ...config,
    }
  }

  /**
   * 生成请求唯一标识
   */
  private generateKey(url: string, options?: RequestInit): RequestKey {
    const method = options?.method || 'GET'
    const body = options?.body ? JSON.stringify(options.body) : ''
    return `${method}:${url}:${body}`
  }

  /**
   * 调试日志
   */
  protected log(...args: any[]): void {
    if (this.config.debug) {
      console.log('[RequestQueue]', ...args)
    }
  }

  /**
   * 阻塞队列（停止处理新请求）
   */
  block(): void {
    this.log('Queue blocked')
    this.isBlocked = true
  }

  /**
   * 解除阻塞（恢复处理请求）
   */
  unblock(): void {
    this.log('Queue unblocked')
    this.isBlocked = false
    this.processQueue()
  }

  /**
   * 清空队列
   */
  clear(): void {
    this.log('Queue cleared')
    this.queue = []
    this.pendingRequests.clear()
  }

  /**
   * 添加请求到队列
   */
  async enqueue<T>(
    key: RequestKey,
    executor: () => Promise<T>
  ): Promise<T> {
    this.log('Enqueue request:', key)

    // 检查是否有重复请求（去重）
    if (this.config.enableDeduplication) {
      const existingRequest = this.pendingRequests.get(key)
      if (existingRequest) {
        this.log('Duplicate request found, reusing:', key)
        return existingRequest as Promise<T>
      }
    }

    // 创建 Promise
    const promise = new Promise<T>((resolve, reject) => {
      this.queue.push({
        key,
        executor,
        resolve,
        reject,
        timestamp: Date.now(),
      })
    })

    // 记录到待处理请求
    if (this.config.enableDeduplication) {
      this.pendingRequests.set(key, promise)

      // 设置超时清理
      setTimeout(() => {
        this.pendingRequests.delete(key)
      }, this.config.deduplicationWindow)
    }

    // 开始处理队列
    this.processQueue()

    return promise
  }

  /**
   * 处理队列
   */
  private async processQueue(): Promise<void> {
    // 如果正在处理或被阻塞，则不处理
    if (this.isProcessing || this.isBlocked) {
      this.log('Queue processing skipped (processing:', this.isProcessing, 'blocked:', this.isBlocked, ')')
      return
    }

    // 如果队列为空，则不处理
    if (this.queue.length === 0) {
      this.log('Queue is empty')
      return
    }

    this.isProcessing = true
    this.log('Start processing queue, size:', this.queue.length)

    while (this.queue.length > 0 && !this.isBlocked) {
      const request = this.queue.shift()
      if (!request) break

      this.log('Processing request:', request.key)

      try {
        const result = await request.executor()
        request.resolve(result)
        this.log('Request succeeded:', request.key)
      } catch (error) {
        request.reject(error)
        this.log('Request failed:', request.key, error)
      } finally {
        // 从待处理列表中移除
        this.pendingRequests.delete(request.key)
      }
    }

    this.isProcessing = false
    this.log('Queue processing finished')
  }

  /**
   * 包装请求函数（添加去重和队列管理）
   */
  wrap<T>(
    executor: () => Promise<T>,
    key?: RequestKey
  ): Promise<T> {
    const requestKey = key || `request-${Date.now()}-${Math.random()}`
    return this.enqueue(requestKey, executor)
  }

  /**
   * 获取队列状态
   */
  getStatus(): {
    queueSize: number
    isProcessing: boolean
    isBlocked: boolean
    pendingCount: number
  } {
    return {
      queueSize: this.queue.length,
      isProcessing: this.isProcessing,
      isBlocked: this.isBlocked,
      pendingCount: this.pendingRequests.size,
    }
  }
}

/**
 * Token 刷新队列（单例）
 * 用于在 Token 刷新时阻塞所有请求
 */
class TokenRefreshQueue extends RequestQueue {
  private refreshPromise: Promise<any> | null = null

  /**
   * 执行 Token 刷新（阻塞其他请求）
   */
  async refresh(refreshFn: () => Promise<any>): Promise<any> {
    // 如果正在刷新，等待刷新完成
    if (this.refreshPromise) {
      this.log('Token refresh already in progress, waiting...')
      return this.refreshPromise
    }

    // 阻塞队列
    this.block()

    try {
      // 执行刷新
      this.refreshPromise = refreshFn()
      const result = await this.refreshPromise

      this.log('Token refresh succeeded')
      return result
    } catch (error) {
      this.log('Token refresh failed:', error)
      throw error
    } finally {
      // 解除阻塞
      this.refreshPromise = null
      this.unblock()
    }
  }
}

/**
 * 创建默认请求队列实例
 */
export const defaultRequestQueue = new RequestQueue()

/**
 * 创建 Token 刷新队列实例
 */
export const tokenRefreshQueue = new TokenRefreshQueue({ debug: false })

export default RequestQueue
